import React, { useState } from "react";
import classnames from "classnames";

import { Action, Category } from "@/constants/events";
import { Deal } from "@/custom-types/Deals";
import { Promotion } from "@/custom-types/Promotions";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { useSelector } from "@/redux/hooks";
import { getUserId } from "@/redux/selectors/user";
import { getDispensaryUrl } from "@/utils/dispensaryUrls";
import { getRoundedDistance } from "@/utils/distance";

import DealCard, { DealCardPlaceholder } from "@/components/botanic//DealCard";
import Button from "@/components/botanic/Button";
import DealsPromosNoResults from "@/components/DealsPromosNoResults";

const PROMOS_PER_PAGE = 6;

const PromoResults: React.FC<{
  className?: string;
  promotions?: Promotion[];
}> = ({ className, promotions }) => {
  const countryCode = useDomainCountryCode();

  const { publishEvent } = useEventTracker();

  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const userId = useSelector(getUserId);

  const handleSeeMoreClick = async () => {
    setIsLoading(true);

    publishEvent({
      action: Action.click,
      category: Category.loadMorePromotionsButton,
      label: "",
    });

    setPage(page + 1);
    setIsLoading(false);
  };

  const totalCount = promotions?.length || 0;
  const slicedPromotions = totalCount
    ? promotions?.slice(0, page * PROMOS_PER_PAGE)
    : undefined;
  const currentCount = slicedPromotions?.length || 0;

  return (
    <div className={className} data-testid="dispensary-promotions">
      <h2 className="heading--m">
        {countryCode === "CA"
          ? "Cannabis retailer store promotions"
          : "Dispensary promotions now"}
      </h2>
      <p className="mb-md">
        Know before you go! These promos are available to claim in-store. Ask
        your budtender before you checkout!
      </p>

      <div className="row">
        {slicedPromotions ? (
          slicedPromotions.map((promo) => {
            const {
              dispensary: { id: dispensaryId, slug, distanceMi },
            } = promo;

            const promoUrl = getDispensaryUrl({
              countryCode,
              slug,
              tab: "deals",
            });

            return (
              <div key={dispensaryId} className="col md:col-1/3 mb-md">
                <DealCard
                  data-testid="promo-deal-card"
                  deal={
                    {
                      ...promo,
                      dealUrl: promoUrl,
                    } as unknown as Deal
                  }
                  showFulfillment={true}
                  countryCode={countryCode}
                  className="rounded h-full overflow-hidden shadow-low"
                  onClick={() =>
                    publishEvent({
                      action: Action.click,
                      category: Category.dealsMenuBrowsePromotions,
                      dispensaryDistance: getRoundedDistance(
                        distanceMi,
                        countryCode,
                      ),
                      dispensaryId,
                      label: promo.title.toLowerCase(),
                      userId: userId as number,
                    })
                  }
                  onLoad={() =>
                    publishEvent({
                      action: Action.impression,
                      category: Category.dealsMenuBrowsePromotions,
                      dispensaryDistance: getRoundedDistance(
                        distanceMi,
                        countryCode,
                      ),
                      dispensaryId,
                      label: promo.title.toLowerCase(),
                      nonInteractionEvent: true,
                      userId: userId as number,
                    })
                  }
                />
              </div>
            );
          })
        ) : (
          <div className="col">
            <DealsPromosNoResults type="promotions" className="h-[390px]" />
          </div>
        )}

        {isLoading &&
          Array(
            totalCount - currentCount >= PROMOS_PER_PAGE
              ? PROMOS_PER_PAGE
              : totalCount - currentCount,
          )
            .fill(null)
            .map((_, i) => (
              <div
                className="col md:col-1/3 mb-md"
                key={`promo-results-placeholder-${i}`}
              >
                <DealCardPlaceholder />
              </div>
            ))}
      </div>

      {totalCount > PROMOS_PER_PAGE && currentCount < totalCount && (
        <Button
          className={classnames("mt-lg", {
            "bg-light-grey": isLoading,
          })}
          data-testid="load-more-promos"
          disabled={isLoading}
          onClick={handleSeeMoreClick}
        >
          load more promotions
        </Button>
      )}
    </div>
  );
};

export default PromoResults;
